import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wrench, MailCheck, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-blue-700 flex items-center justify-center text-white">
            <Wrench className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-xl text-slate-900">FixTrack</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Appliance Repair CRM</div>
          </div>
        </div>

        {sent ? (
          <Card className="border-emerald-200" data-testid="forgot-sent-card">
            <CardContent className="p-8 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                <MailCheck className="w-7 h-7 text-emerald-600" />
              </div>
              <div className="font-heading text-xl font-bold text-slate-900">Check your email</div>
              <p className="text-sm text-slate-600">
                If an account matches <strong>{email}</strong>, a password reset link is on its way.
                The link expires in 45 minutes.
              </p>
              <Link to="/login">
                <Button variant="outline" className="mt-2 gap-2" data-testid="forgot-back-to-login">
                  <ArrowLeft className="w-4 h-4" /> Back to sign in
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-2xl">Forgot password</CardTitle>
              <p className="text-sm text-slate-500">
                Enter your account email and we&apos;ll send you a link to reset your password.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fp-email">Email</Label>
                  <Input
                    id="fp-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    data-testid="forgot-email"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-700 hover:bg-blue-800"
                  data-testid="forgot-submit"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
                <div className="text-xs text-slate-500 text-center">
                  <Link to="/login" className="text-blue-700 font-medium" data-testid="forgot-back-link">
                    Back to sign in
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
